"use client";

import type { ChannelFeedEngagement } from "@mlx/contracts";
import { MessageSquareIcon } from "lucide-react";
import Link from "next/link";

import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const REACTION_EMOJI = {
  thumbs_up: "👍",
  heart: "❤️",
  laugh: "😂",
  celebrate: "🎉",
  eyes: "👀",
  check: "✅",
} as const;

const REVEAL_ON_HOVER =
  "sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100";

/*
 * The engagement line under a feed row. Reactions and reply counts are always
 * visible. A row with neither shows its Reply affordance only on hover or
 * focus, so a quiet stream stays quiet.
 */
export function FeedEngagement({
  engagement,
  onOpen,
  href,
  className,
}: {
  engagement: ChannelFeedEngagement | null;
  onOpen?: () => void;
  href?: string;
  className?: string;
}) {
  const reactions = engagement?.reactions ?? [];
  const commentCount = engagement?.commentCount ?? 0;
  const unreadCount = engagement?.unreadCount ?? 0;
  const quiet = reactions.length === 0 && commentCount === 0;
  const label =
    commentCount > 0
      ? `${commentCount} ${commentCount === 1 ? "reply" : "replies"}`
      : "Reply";
  const tone = unreadCount > 0 ? "text-foreground" : "text-muted-foreground";
  return (
    <div
      className={cn(
        "mt-1.5 flex flex-wrap items-center gap-1",
        quiet ? REVEAL_ON_HOVER : null,
        className,
      )}
    >
      {reactions.map((reaction) => (
        <button
          key={reaction.reaction}
          type="button"
          aria-label={`Open thread with ${reaction.count} ${reaction.reaction} reactions`}
          className="inline-flex h-6 items-center gap-1 rounded-full border border-border/70 bg-background px-1.5 text-2xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          onClick={onOpen}
        >
          <span aria-hidden>{REACTION_EMOJI[reaction.reaction]}</span>
          {reaction.count}
        </button>
      ))}
      {href && !onOpen ? (
        <Link
          href={href}
          className={cn(
            buttonVariants({ variant: "ghost", size: "xs" }),
            "-ml-1",
            tone,
          )}
        >
          <MessageSquareIcon aria-hidden />
          {label}
        </Link>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className={cn("-ml-1", tone)}
          onClick={onOpen}
        >
          <MessageSquareIcon aria-hidden />
          {label}
          {unreadCount > 0 ? (
            <span className="font-medium text-primary">
              {` · ${unreadCount} new`}
            </span>
          ) : null}
        </Button>
      )}
    </div>
  );
}
